import type { Owner } from "src/business/account/owner";

type FeatureConfigurationType = Record<
  string,
  {
    capability: string;
    permissions: string[];
  }
>;

export const FEATURE_CAPABILITY_CONFIG: FeatureConfigurationType = {
  booking_management: {
    capability: "booking_management",
    permissions: [
      "booking_read",
      "booking_update",
      "booking_creation",
      "booking_blacklist",
    ],
  },
  // The visibilite tier's slice of the booking module: today's service and
  // nothing beyond it. It grants no blacklist — repeat-offender handling is
  // sold with full booking management.
  same_day_booking_management: {
    capability: "same_day_booking_management",
    permissions: ["booking_read", "booking_update", "booking_creation"],
  },
  table_scheduling: {
    capability: "table_scheduling",
    permissions: ["table_schedule_read", "table_schedule_update"],
  },
  analytics_dashboard: {
    capability: "analytics_dashboard",
    permissions: ["analytics_read"],
  },
  restaurant_management: {
    capability: "restaurant_management",
    permissions: [
      "restaurant_information_read",
      "restaurant_information_update",
    ],
  },
  opening_hours_management: {
    capability: "opening_hours_management",
    permissions: ["opening_hours_read", "opening_hours_update"],
  },
  menu_management: {
    capability: "menu_management",
    permissions: ["menu_read", "menu_update"],
  },
  deals_management: {
    capability: "deals_management",
    permissions: ["deals_read", "deals_create", "deals_update"],
  },
  image_gallery_management: {
    capability: "image_gallery_management",
    permissions: ["gallery_read", "gallery_update"],
  },
  organization_management: {
    capability: "organization_management",
    permissions: ["organization_read", "organization_update"],
  },
};

export const FEATURES = Object.keys(FEATURE_CAPABILITY_CONFIG);

export const isFeatureSectionEnabled = (
  permissions: string[],
  feature: string,
  owner: Owner | null,
  restaurantCapabilities: string[] | undefined,
) => {
  // First check if the feature is enabled before checking the section
  if (!isFeatureEnabled(feature, restaurantCapabilities, owner) || !owner) {
    return false;
  }

  return (
    owner.admin ||
    owner.isOrganizationOwner ||
    permissions.every((perm) => owner.permissions.includes(perm)) // Check if user has all required permissions for the section
  );
};

// Why a feature is — or is not — available. `isFeatureEnabled` only answers
// yes/no, but the dashboard shows a different card for each denial: a missing
// capability is sold (upgrade the subscription), a missing permission is
// granted by the organization administrator.
export enum FeatureAccess {
  Granted = "granted",
  /** The restaurant's subscription does not bundle the feature's capability. */
  MissingCapability = "missing_capability",
  /** The restaurant has the capability, but the user was not granted it. */
  MissingPermission = "missing_permission",
}

export const getFeatureAccess = (
  feature: string,
  restaurantCapabilities: string[] | undefined,
  owner: Owner | null,
): FeatureAccess => {
  if (!owner) return FeatureAccess.MissingPermission; // If no user, feature is not enabled
  if (!restaurantCapabilities) return FeatureAccess.Granted;

  const config = FEATURE_CAPABILITY_CONFIG[feature];
  if (!config || owner.admin) return FeatureAccess.Granted; // If no config, assume feature is enabled

  if (!restaurantCapabilities.includes(config.capability))
    return FeatureAccess.MissingCapability; // Check restaurant capability

  // Check if user has at least one of the required permissions for the feature
  return owner.isOrganizationOwner ||
    config.permissions.some((perm) => owner.permissions.includes(perm))
    ? FeatureAccess.Granted
    : FeatureAccess.MissingPermission;
};

export const isFeatureEnabled = (
  feature: string,
  restaurantCapabilities: string[] | undefined,
  owner: Owner | null,
) =>
  getFeatureAccess(feature, restaurantCapabilities, owner) ===
  FeatureAccess.Granted;

/**
 * Whether any one of several features is open. A module sold in slices — the
 * booking list is reachable with full booking management *or* with the
 * same-day capability alone — is gated on the union, and each surface inside it
 * then decides how much of itself to show.
 */
export const isAnyFeatureEnabled = (
  features: string[],
  restaurantCapabilities: string[] | undefined,
  owner: Owner | null,
) =>
  features.some((feature) =>
    isFeatureEnabled(feature, restaurantCapabilities, owner),
  );

type QuotaTier = {
  capability: string;
  value: number;
};

type QuotaConfigurationType = Record<string, QuotaTier[]>;

/** A quota capability that grants no ceiling at all. */
export const UNLIMITED_QUOTA = Number.POSITIVE_INFINITY;

/** Whether an allowance is unbounded and should read as "unlimited". */
export const isUnlimitedQuota = (quota: number): boolean =>
  !Number.isFinite(quota);

// Size/quota capabilities are tiers rather than permission-gated sections:
// a restaurant gets the highest value among the tiers it has been granted.
export const FEATURE_QUOTA_CONFIG: QuotaConfigurationType = {
  gallery: [
    { capability: "gallery_size_s", value: 5 },
    { capability: "gallery_size_m", value: 20 },
  ],
  // `staff_size_unlimited` is the reservation tier's "comptes et rôles
  // illimités". Infinity rather than a very large number: it survives being
  // summed or compared like any other allowance, and it is the one value a
  // caller can tell apart from a real ceiling with `Number.isFinite`, so
  // "illimité" never has to be rendered as a nine-digit quota.
  staff: [
    { capability: "staff_size_s", value: 3 },
    { capability: "staff_size_m", value: 5 },
    { capability: "staff_size_unlimited", value: UNLIMITED_QUOTA },
  ],
};

export const getFeatureQuota = (
  quota: string,
  restaurantCapabilities: string[] | undefined,
): number => {
  const tiers = FEATURE_QUOTA_CONFIG[quota];
  if (!tiers || !restaurantCapabilities) return 0;

  return tiers.reduce(
    (highest, tier) =>
      restaurantCapabilities.includes(tier.capability)
        ? Math.max(highest, tier.value)
        : highest,
    0,
  );
};

// Organization access is org-scoped, not restaurant-scoped: the My Organization
// route has no restaurantId and organization permissions aren't tied to a
// restaurant capability. These mirror the admin / organization-owner / permission
// semantics of isFeatureEnabled but check the owner's permissions directly.
const hasOrganizationPermission = (
  owner: Owner | null,
  permission: string,
): boolean =>
  !!owner &&
  (owner.admin ||
    owner.isOrganizationOwner ||
    owner.permissions.includes(permission));

export const isOrganizationViewEnabled = (owner: Owner | null): boolean =>
  !owner?.admin && hasOrganizationPermission(owner, "organization_read");

export const isOrganizationManageEnabled = (owner: Owner | null): boolean =>
  hasOrganizationPermission(owner, "organization_update");

// The payment surface is the one route that deliberately escapes the three-tier
// model: no capability, no permission, no subscription gate. Binding it to a
// capability would take the page away from the restaurant that most needs it —
// the lapsed one — so it mirrors the backend's `PaymentAccessPolicy` and keys on
// organization ownership alone. `isOrganizationOwner` is optional on `Owner`:
// an older /staff/me payload without it resolves to false rather than exposing
// the surface by accident.
export const isPaymentSurfaceEnabled = (owner: Owner | null): boolean =>
  !!owner && (owner.admin || owner.isOrganizationOwner === true);

/**
 * Whether the viewer may see what the restaurant pays: the payments section and
 * the dashboard's subscription header. Billing is organization business, so it
 * rides on the organization read permission — a receptionist granted only the
 * booking permissions is not shown the plan, its price or the next billing date.
 *
 * The payments surface stays conjoined with {@link isPaymentSurfaceEnabled}: the
 * backend authorizes every payment endpoint on organization ownership alone, so
 * a permission-holding non-owner would only reach a page that 403s.
 */
export const isBillingInfoVisible = (owner: Owner | null): boolean =>
  hasOrganizationPermission(owner, "organization_read");
